import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  Download,
  Plus,
  Trash2,
  Calculator,
  Package,
  FileText,
  Building2,
  MapPin,
  FileDown
} from 'lucide-react';
import { useProjects } from '../contexts/ProjectContext';
import { generateProjectPDF } from '../utils/pdfGenerator';

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { 
    currentProject, 
    loadProject, 
    saveCurrentProject, 
    addDevice, 
    removeDevice,
    removeCalculation,
    isLoading 
  } = useProjects();

  const [activeTab, setActiveTab] = useState<'overview' | 'devices' | 'calculations' | 'notes'>('overview');
  const [isSaving, setIsSaving] = useState(false);
  const [showAddDevice, setShowAddDevice] = useState(false);

  useEffect(() => {
    if (projectId) {
      loadProject(projectId).catch(() => {
        alert('Project not found');
        navigate('/projects');
      });
    }
  }, [projectId]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveCurrentProject();
      alert('Project saved successfully!');
    } catch (error) {
      alert('Failed to save project');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = () => {
    if (!currentProject) return;

    const dataStr = JSON.stringify(currentProject, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${currentProject.name.replace(/\s+/g, '_')}_${Date.now()}.json`;
    link.click();
  };

  const handleExportPDF = () => {
    if (!currentProject) return;

    try {
      generateProjectPDF(currentProject, {
        companyName: 'Cal-Spec Fire Alarm',
        companyAddress: '123 Main Street, Los Angeles, CA 90001',
        companyPhone: '(818) 533-8313',
        companyEmail: 'info@cal-spec.com',
        includeDevices: true,
        includeCalculations: true,
        includeNotes: true
      });
      alert('PDF report generated successfully!');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
    }
  };

  if (isLoading || !currentProject) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading project...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/projects"
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{currentProject.name}</h1>
                <p className="text-sm text-slate-600">
                  Last updated: {new Date(currentProject.updatedAt).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleExportPDF}
                className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                <FileDown className="w-4 h-4 mr-2" />
                PDF Report
              </button>
              <button
                onClick={handleExport}
                className="flex items-center px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg transition-colors duration-200"
              >
                <Download className="w-4 h-4 mr-2" />
                Export JSON
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors duration-200 disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 font-semibold transition-colors duration-200 border-b-2 ${
                activeTab === 'overview'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <Building2 className="w-4 h-4 inline mr-2" />
              Overview
            </button>
            <button
              onClick={() => setActiveTab('devices')}
              className={`px-6 py-3 font-semibold transition-colors duration-200 border-b-2 ${
                activeTab === 'devices'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <Package className="w-4 h-4 inline mr-2" />
              Devices ({currentProject.devices.length})
            </button>
            <button
              onClick={() => setActiveTab('calculations')}
              className={`px-6 py-3 font-semibold transition-colors duration-200 border-b-2 ${
                activeTab === 'calculations'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <Calculator className="w-4 h-4 inline mr-2" />
              Calculations ({currentProject.calculations.length})
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`px-6 py-3 font-semibold transition-colors duration-200 border-b-2 ${
                activeTab === 'notes'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Notes
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Project Info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Project Information</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600">Client Name</label>
                    <p className="text-slate-900">{currentProject.clientName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Building Type</label>
                    <p className="text-slate-900 capitalize">{currentProject.buildingType}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Square Footage</label>
                    <p className="text-slate-900">{currentProject.squareFootage.toLocaleString()} sq ft</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Number of Floors</label>
                    <p className="text-slate-900">{currentProject.numberOfFloors}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">System Type</label>
                    <p className="text-slate-900 capitalize">{currentProject.systemType}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Status</label>
                    <p className="text-slate-900 capitalize">{currentProject.status.replace('_', ' ')}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  Location
                </h2>
                <div className="space-y-2">
                  <p className="text-slate-900">{currentProject.projectAddress || 'No address provided'}</p>
                  <p className="text-slate-600">
                    {currentProject.city && currentProject.state
                      ? `${currentProject.city}, ${currentProject.state} ${currentProject.zipCode}`
                      : 'No location provided'}
                  </p>
                </div>
              </div>

              {currentProject.description && (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h2 className="text-xl font-bold text-slate-900 mb-4">Description</h2>
                  <p className="text-slate-700">{currentProject.description}</p>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-indigo-600 to-purple-600 text-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold mb-4">Project Stats</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-indigo-100">Total Devices</span>
                    <span className="text-2xl font-bold">{currentProject.devices.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-indigo-100">Calculations</span>
                    <span className="text-2xl font-bold">{currentProject.calculations.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-indigo-100">Coverage Area</span>
                    <span className="text-2xl font-bold">{currentProject.squareFootage.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setActiveTab('devices')}
                    className="w-full py-2 px-4 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-semibold rounded-lg transition-colors duration-200"
                  >
                    Add Devices
                  </button>
                  <button
                    onClick={() => setActiveTab('calculations')}
                    className="w-full py-2 px-4 bg-green-100 hover:bg-green-200 text-green-700 font-semibold rounded-lg transition-colors duration-200"
                  >
                    Add Calculations
                  </button>
                  <button
                    onClick={handleExportPDF}
                    className="w-full py-2 px-4 bg-red-100 hover:bg-red-200 text-red-700 font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center"
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    Generate PDF Report
                  </button>
                  <button
                    onClick={handleExport}
                    className="w-full py-2 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg transition-colors duration-200"
                  >
                    Export JSON
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'devices' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Devices</h2>
              <button
                onClick={() => setShowAddDevice(true)}
                className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Device
              </button>
            </div>

            {currentProject.devices.length === 0 ? (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No devices yet</h3>
                <p className="text-slate-500 mb-6">Add devices to your fire alarm system</p>
                <button
                  onClick={() => setShowAddDevice(true)}
                  className="inline-flex items-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors duration-200"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add First Device
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {currentProject.devices.map(device => (
                  <div key={device.id} className="bg-white rounded-xl shadow-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-bold text-slate-900 capitalize">
                          {device.type.replace('_', ' ')}
                        </h3>
                        <p className="text-sm text-slate-600">{device.manufacturer} {device.model}</p>
                      </div>
                      <button
                        onClick={() => removeDevice(device.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p className="text-slate-600">
                        <span className="font-medium">Location:</span> {device.location}
                      </p>
                      <p className="text-slate-600">
                        <span className="font-medium">Quantity:</span> {device.quantity}
                      </p>
                      {device.notes && (
                        <p className="text-slate-600">
                          <span className="font-medium">Notes:</span> {device.notes}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {showAddDevice && (
              <AddDeviceModal
                onClose={() => setShowAddDevice(false)}
                onAdd={(device) => {
                  addDevice(device);
                  setShowAddDevice(false);
                }}
              />
            )}
          </div>
        )}

        {activeTab === 'calculations' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Calculations</h2>
              <Link
                to="/custom-calculations"
                className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Calculation
              </Link>
            </div>

            {currentProject.calculations.length === 0 ? (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <Calculator className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No calculations yet</h3>
                <p className="text-slate-500 mb-6">Add calculations for your project</p>
                <Link
                  to="/custom-calculations"
                  className="inline-flex items-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors duration-200"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Calculation
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {currentProject.calculations.map(calc => (
                  <div key={calc.id} className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">{calc.name}</h3>
                        <p className="text-sm text-slate-600 capitalize">{calc.type.replace('_', ' ')}</p>
                      </div>
                      <button
                        onClick={() => removeCalculation(calc.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-medium text-slate-700 mb-2">Inputs:</p>
                        {Object.entries(calc.inputs).map(([key, value]) => (
                          <p key={key} className="text-slate-600">
                            {key}: {value}
                          </p>
                        ))}
                      </div>
                      <div>
                        <p className="font-medium text-slate-700 mb-2">Results:</p>
                        {Object.entries(calc.results).map(([key, value]) => (
                          <p key={key} className="text-slate-900 font-semibold">
                            {key}: {value}
                          </p>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-4">
                      {new Date(calc.timestamp).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Project Notes</h2>
            <textarea
              value={currentProject.notes}
              onChange={(e) => {
                // Update notes in real-time (will be saved when user clicks Save)
                currentProject.notes = e.target.value;
              }}
              rows={15}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Add notes about your project..."
            />
            <p className="text-sm text-slate-500 mt-2">
              Notes are automatically included when you save the project
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function AddDeviceModal({ onClose, onAdd }: { 
  onClose: () => void; 
  onAdd: (device: any) => void;
}) {
  const [formData, setFormData] = useState({
    type: 'smoke_detector',
    manufacturer: '',
    model: '',
    location: '',
    quantity: '1',
    notes: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd({
      ...formData,
      quantity: parseInt(formData.quantity) || 1
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-xl font-bold text-slate-900">Add Device</h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Device Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="smoke_detector">Smoke Detector</option>
              <option value="heat_detector">Heat Detector</option>
              <option value="pull_station">Pull Station</option>
              <option value="horn_strobe">Horn/Strobe</option>
              <option value="speaker">Speaker</option>
              <option value="module">Module</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Manufacturer</label>
            <input
              type="text"
              value={formData.manufacturer}
              onChange={(e) => setFormData(prev => ({ ...prev, manufacturer: e.target.value }))}
              required
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Notifier"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Model</label>
            <input
              type="text"
              value={formData.model}
              onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
              required
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="FSP-851"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
              required
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="2nd Floor Corridor"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Quantity</label>
            <input
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
              min="1"
              required
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Notes (Optional)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Additional notes..."
            />
          </div>
        </form>

        <div className="p-6 border-t border-slate-200 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors duration-200"
          >
            Add Device
          </button>
        </div>
      </div>
    </div>
  );
}
